/**
 * BD SDK logging utilities.
 *
 * Provides a narrow `Logger` interface and a factory `getLogger` that adapts to the
 * runtime environment:
 *
 * - In browser-like environments, returns a console-based logger that only emits
 *   error-level messages, avoiding Node.js-only dependencies.
 * - In non-browser environments, dynamically imports `winston` and returns a fully
 *   featured logger, optionally with file rotation when enabled.
 *
 * @packageDocumentation
 */
/** Determine whether we are running in a browser-like environment. */
function isBrowserLikeEnv() {
    return typeof globalThis.window !== "undefined";
}
/** Check if logging is disabled via environment variable */
function isLoggingDisabled() {
    try {
        const env = typeof process !== "undefined" ? process.env : {};
        const enableLog = env.ENABLE_LOG;
        return enableLog === "false" || enableLog === "0";
    }
    catch {
        return false;
    }
}
/**
 * Safely obtain a Node-style `require` function at runtime without statically
 * importing Node built-ins. This avoids bundlers (e.g., Vite) from externalizing
 * Node core modules in browser builds while still allowing Node/Bun runtimes to
 * synchronously use `require` dependencies ("node:*").
 */
function getNodeRequireSafely() {
    try {
        // Use an indirect function constructor to avoid static analysis by bundlers
        // and only resolve `require` at runtime in Node/Bun environments.
        const getReq = new Function("return (typeof require !== 'undefined' ? require : null);");
        return getReq();
    }
    catch {
        return null;
    }
}
/**
 * Create a fully silent logger that emits no output at any level.
 *
 * @remarks
 * Used by {@link getLogger} when logging is disabled via environment in non-browser
 * environments, or when a completely quiet logger is desired. In production, set
 * the environment variable `ENABLE_LOG=false` (or `0`) to force a silent logger.
 *
 * - Browser-like environments default to a console error-only logger to avoid
 *   importing Node.js dependencies.
 * - Non-browser environments can opt into full silence by configuring `ENABLE_LOG`.
 *
 * @example
 * ```bash
 * # Suppress all SDK logs in production
 * export ENABLE_LOG=false
 * ```
 */
function createSilentLogger() {
    return {
        silly: () => { },
        debug: () => { },
        info: () => { },
        warn: () => { },
        error: () => { },
        addContext: () => { },
    };
}
/** Create a browser-friendly logger that only emits errors to console. */
function createConsoleErrorOnlyLogger() {
    return {
        silly: () => { },
        debug: () => { },
        info: () => { },
        warn: () => { },
        error: (message, ...meta) => {
            try {
                if (typeof console !== "undefined" && typeof console.error === "function") {
                    if (meta && meta.length)
                        console.error(message, ...meta);
                    else
                        console.error(message);
                }
            }
            catch {
                // Silently fail
            }
        },
        addContext: () => { },
    };
}
/** Build the console transport with the configured format. */
function buildConsoleTransport(winstonMod) {
    return new winstonMod.transports.Console();
}
/** Build a file transport synchronously using Node's require, handling defaults. */
function buildFileTransportSync(winstonMod, options) {
    const nodeRequire = getNodeRequireSafely();
    if (!nodeRequire) {
        throw new Error("Node-style require not available for file transport");
    }
    const path = nodeRequire("node:path");
    const fs = nodeRequire("node:fs");
    const defaultLogsDir = "logs";
    const suppliedPath = options.file?.filename;
    const derivedFilename = suppliedPath ? path.basename(suppliedPath) : `${options.label}.log`;
    const derivedDirname = suppliedPath ? path.dirname(suppliedPath) : defaultLogsDir;
    // Best-effort ensure directory and file exist
    try {
        if (!fs.existsSync(derivedDirname)) {
            fs.mkdirSync(derivedDirname, { recursive: true });
        }
        const fullPath = path.join(derivedDirname, derivedFilename);
        if (!fs.existsSync(fullPath)) {
            try {
                fs.writeFileSync(fullPath, "");
            }
            catch {
                // Silently fail
            }
        }
    }
    catch {
        // Silently fail
    }
    const maxSize = options.file?.maxSizeBytes ?? 50 * 1024 * 1024;
    const maxFiles = options.file?.maxFiles ?? 10;
    const winston = winstonMod;
    return new winston.transports.File({
        dirname: derivedDirname,
        filename: derivedFilename,
        maxsize: maxSize,
        maxFiles: maxFiles,
    });
}
/**
 * Creates a configured logger instance for BD SDK modules.
 *
 * @remarks
 * This function provides a centralized logging solution for all BD SDK modules.
 * Environment behavior:
 * - Browser-like environments (i.e., when `window` exists) return a console-based logger
 *   that only emits errors.
 * - When logs are disabled via environment (e.g., `ENABLE_LOG=false`) in non-browser
 *   environments, a fully silent logger is returned.
 *
 * When file persistence is enabled (via `options.file.enabled` or the legacy
 * `persistLogs` flag), a size-rotated file transport is added alongside console output.
 *
 * **Features:**
 * - **Flexible Log Levels**: Support for standard log levels (silly, debug, info, warn, error)
 * - **Module Identification**: Each logger instance is labeled for easy identification
 * - **Optional File Persistence**: Enable file logging for production environments
 * - **Automatic Log Rotation**: Prevents log files from growing too large (50MB limit)
 * - **Thread-Safe**: Safe for concurrent usage across multiple modules
 * - **Browser Compatible**: Automatically detects browser environments and uses console-only logging
 *
 * **Usage Guidelines:**
 * - Use descriptive labels to identify the source module or component
 * - Set appropriate log levels based on environment (debug for dev, info for prod)
 * - Enable persistent logs in production for debugging and monitoring
 * - Avoid logging sensitive information like API keys or user data
 *
 * @param options - Logger configuration options including label and level
 * @param persistLogs - Whether to enable file-based logging (defaults to false)
 * @returns Configured Logger instance ready for use
 *
 * @example
 * **Basic Console Logging**
 * ```typescript
 * import { getLogger } from "@blockdaemon/transaction-verifier";
 *
 * const logger = getLogger({
 *   label: "my-module",
 *   level: "info"
 * });
 *
 * logger.info("Module initialized successfully");
 * logger.debug("Debug information", { data: "example" });
 * logger.error("An error occurred", error);
 * ```
 *
 * @example
 * **File-based Logging for Production**
 * ```typescript
 * import { getLogger } from "@blockdaemon/transaction-verifier";
 *
 * // Enable persistent logging
 * const logger = getLogger({
 *   label: "transaction-verifier",
 *   level: "warn"
 * }, true);
 *
 * logger.warn("Rate limit approaching", {
 *   requests: 95,
 *   limit: 100
 * });
 * logger.error("Transaction failed", {
 *   txHash: "0x123...",
 *   reason: "insufficient gas"
 * });
 * ```
 *
 * @example
 * **Module-specific Logger Setup**
 * ```typescript
 * import { getLogger } from "@blockdaemon/transaction-verifier";
 *
 * class DecodingModule {
 *   private logger: ILogger;
 *
 *   constructor(options: { logLevel?: string }) {
 *     this.logger = getLogger({
 *       label: "decoding-module",
 *       level: options.logLevel || "info"
 *     });
 *
 *     this.logger.info("Module initialized");
 *   }
 *
 *   async decode(data: string) {
 *     this.logger.debug("Decoding data", { dataLength: data.length });
 *     // ... implementation
 *     this.logger.info("Decoding completed successfully");
 *   }
 * }
 * ```
 *
 * @public
 */
export function getLogger(options, persistLogs = false) {
    const level = String(options.level || "info").toLowerCase();
    // Browser-like: default to console error-only logging
    if (isBrowserLikeEnv()) {
        return createConsoleErrorOnlyLogger();
    }
    // If ENABLE_LOG is set to false, return a fully silent logger
    if (isLoggingDisabled()) {
        return createSilentLogger();
    }
    // Non-browser: synchronously load Winston via Node's require
    try {
        const nodeRequire = getNodeRequireSafely();
        if (!nodeRequire) {
            return createConsoleErrorOnlyLogger();
        }
        const winstonMod = nodeRequire("winston");
        const transports = [buildConsoleTransport(winstonMod)];
        const fileEnabled = options.file?.enabled ?? persistLogs;
        if (fileEnabled) {
            try {
                const fileTransport = buildFileTransportSync(winstonMod, options);
                transports.push(fileTransport);
            }
            catch {
                // Silently fail to add file transport
            }
        }
        const winston = winstonMod;
        const base = winston.createLogger({
            level: level,
            silent: false,
            defaultMeta: { label: options.label },
            format: winston.format.combine(winston.format.timestamp()),
            transports: transports,
        });
        // Wrap with addContext method for API compatibility
        const logger = base;
        const context = {};
        logger.addContext = (key, value) => {
            context[key] = value;
            // Store context but Winston's defaultMeta already handles label
            // This is kept for API compatibility with existing code
        };
        return logger;
    }
    catch {
        return createConsoleErrorOnlyLogger();
    }
}
//# sourceMappingURL=data:application/json;base64,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